import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { bookingAPI } from '../services/api';
import { getStayInfoFromBooking } from '../utils/stayBookingDisplay';
import { getStayBookingBadge } from '../utils/stayBookingStatus';

const BookingDetail = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [bookingData, setBookingData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchBooking = async () => {
            try {
                setLoading(true);
                const bookingId = location.state?.bookingId || location.pathname.split('/').pop();
                if (location.state?.bookingData) {
                    setBookingData(location.state.bookingData);
                    setLoading(false);
                } else if (bookingId) {
                    const response = await bookingAPI.getBookingById(bookingId);
                    if (response.booking) {
                        setBookingData(response.booking);
                    }
                }
            } catch (error) {
                console.error('Failed to fetch booking:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchBooking();
    }, [location]);

    if (loading) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-[#FF5E4C] border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    if (!bookingData) {
        return (
            <div className="min-h-screen bg-white flex items-center justify-center">
                <div className="text-center">
                    <p className="text-[16px] text-gray-600 mb-4">Booking not found</p>
                    <button
                        onClick={() => navigate(-1)}
                        className="px-6 py-2 bg-[#FF5E4C] text-white rounded-[12px]"
                    >
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            weekday: 'short',
            day: 'numeric',
            month: 'short'
        });
    };

    const calculateNights = (checkIn, checkOut) => {
        const checkInDate = new Date(checkIn);
        const checkOutDate = new Date(checkOut);
        const diffTime = Math.abs(checkOutDate - checkInDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return diffDays;
    };

    const getStatusBadge = (status, paymentStatus) =>
        getStayBookingBadge({ status, paymentStatus });

    const checkIn = bookingData.checkIn;
    const checkOut = bookingData.checkOut;
    const nights = calculateNights(checkIn, checkOut);
    const statusBadge = getStatusBadge(bookingData.status, bookingData.paymentStatus);
    const stayInfo = getStayInfoFromBooking(bookingData);

    // Get room details from lineItems
    const roomDetails = bookingData.lineItems?.[0] || {};
    const roomQuantity = roomDetails.quantity || 1;
    const roomName = roomDetails.title || 'Standard Room';
    const bedType = roomDetails.bedType || 'Queen Bed';
    const sleeps = roomDetails.sleeps || 2;
    const roomImage = roomDetails.image || stayInfo.image;

    // Calculate pricing
    const roomPrice = bookingData.pricingSummary?.subTotal || 0;
    const taxes = bookingData.pricingSummary?.taxes || 0;
    const discount = bookingData.pricingSummary?.discount || 0;
    const total = bookingData.pricingSummary?.grandTotal || roomPrice - discount;

    // Guest info
    const guestInfo = bookingData.guestInfo || {};
    const nameParts = (guestInfo.name || '').split(' ');
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    return (
        <div className="min-h-screen bg-[#FAF7F2]">
            <div className="bg-[#FAF7F2] md:px-[120px] p-[20px] md:pt-[60px] md:pb-[20px] gap-[16px] flex items-center justify-start">
                <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="hover:bg-gray-100 rounded-full p-1 shrink-0 flex items-center justify-center"
                    aria-label="Go back"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                        <g clip-path="url(#clip0_3371_46)">
                            <path d="M5 12H19" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                            <path d="M5 12L11 18" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                            <path d="M5 12L11 6" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                        </g>
                        <defs>
                            <clipPath id="clip0_3371_46">
                                <rect width="24" height="24" fill="white" />
                            </clipPath>
                        </defs>
                    </svg>
                </button>
                <div className="flex items-center gap-[6px]">
                    <span className="text-[18px] font-normal text-black uppercase leading-none">
                        # {bookingData.bookingId || 'N/A'}
                    </span>
                    <span
                        className={`p-[6px] rounded-full text-[12px] font-medium leading-none ${statusBadge.bgColor} ${statusBadge.textColor}`}
                    >
                        {statusBadge.text}
                    </span>
                </div>
            </div>

            <div className="md:px-[120px] p-[20px] md:py-[20px] pb-10 w-full space-y-[8px]">
                {bookingData.status === 'DECLINED' && (
                    <div className="bg-red-50 border border-red-100 rounded-[14px] p-4 text-[14px] text-red-900">
                        This booking was declined by the property owner. Your payment may still be
                        processed for a refund according to the stay&apos;s policy. Reserved room
                        nights have been released.
                    </div>
                )}
                {/* Hotel Information Card */}
                <div className="bg-white rounded-[16px] p-[14px] md:p-[24px] shadow-sm ">
                    <div className="flex gap-[8px]">
                        <img
                            src={stayInfo.image || '/assets/logo.svg'}
                            alt={stayInfo.title}
                            className="w-[100px] h-[100px] rounded-[12px] object-cover"
                        />
                        <div className="flex-1 py-[12px]">
                            <h2 className="text-[18px] font-medium text-black mb-[8px] leading-[1.1]">
                                {stayInfo.title}
                            </h2>
                            <p className="text-[12px] text-[#bebebe] leading-[1.1]">
                                {stayInfo.location}
                            </p>
                        </div>
                    </div>
                </div>

                {/* Travel Details Card */}
                <div className="bg-white rounded-[16px] p-[20px] md:p-[28px] shadow-sm ">
                    <h3 className="text-[18px] font-medium text-black leading-[1.1]">Travel Details</h3>
                    {/* Divider */}
                    <div className='flex my-[20px] items-center justify-center'>
                        <svg width="100%" height="1px">
                            <line
                                x1="0" y1="1" x2="100%" y2="1"
                                stroke="#f0f0f0"
                                strokeWidth="2"
                                strokeDasharray="10 4"
                            />
                        </svg>
                    </div>
                    {/* Check-in/Check-out */}
                    <div className="flex items-center justify-between">
                        {/* Check-in */}
                        <div className="flex flex-col gap-[6px]">
                            <div className="flex items-center gap-[4px]">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="14"
                                    height="14"
                                    viewBox="0 0 14 14"
                                    fill="none"
                                >
                                    <g clipPath="url(#clip0_2846_6297)">
                                        <path
                                            d="M2.33203 4.08341C2.33203 3.774 2.45495 3.47725 2.67374 3.25846C2.89253 3.03966 3.18928 2.91675 3.4987 2.91675H10.4987C10.8081 2.91675 11.1049 3.03966 11.3237 3.25846C11.5424 3.47725 11.6654 3.774 11.6654 4.08341V11.0834C11.6654 11.3928 11.5424 11.6896 11.3237 11.9084C11.1049 12.1272 10.8081 12.2501 10.4987 12.2501H3.4987C3.18928 12.2501 2.89253 12.1272 2.67374 11.9084C2.45495 11.6896 2.33203 11.3928 2.33203 11.0834V4.08341Z"
                                            stroke="black"
                                            strokeWidth="0.583333"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <path
                                            d="M9.33203 1.75V4.08333"
                                            stroke="black"
                                            strokeWidth="0.583333"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <path
                                            d="M4.66797 1.75V4.08333"
                                            stroke="black"
                                            strokeWidth="0.583333"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <path
                                            d="M2.33203 6.41675H11.6654"
                                            stroke="black"
                                            strokeWidth="0.583333"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_2846_6297">
                                            <rect width="14" height="14" fill="white" />
                                        </clipPath>
                                    </defs>
                                </svg>
                                <span className="text-[12px] text-[#999999]">Check in</span>
                            </div>
                            <div>
                                <div className="font-normal text-[18px] leading-none mb-[6px]">
                                    {formatDate(checkIn)}
                                </div>
                                <div className="text-[14px] font-medium leading-none text-[#636363]">
                                    2:00 PM
                                </div>
                            </div>
                        </div>

                        {/* Duration */}
                        <div className="text-center">
                            <span className="text-[#FF5E4C] font-medium text-[12px] leading-none">
                                {nights} {nights === 1 ? 'Night' : 'Nights'}
                            </span>
                        </div>

                        {/* Check-out */}
                        <div className="flex flex-col gap-[6px] items-end">
                            <div className="flex items-center gap-[4px]">
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="14"
                                    height="14"
                                    viewBox="0 0 14 14"
                                    fill="none"
                                >
                                    <g clipPath="url(#clip0_2846_6297)">
                                        <path
                                            d="M2.33203 4.08341C2.33203 3.774 2.45495 3.47725 2.67374 3.25846C2.89253 3.03966 3.18928 2.91675 3.4987 2.91675H10.4987C10.8081 2.91675 11.1049 3.03966 11.3237 3.25846C11.5424 3.47725 11.6654 3.774 11.6654 4.08341V11.0834C11.6654 11.3928 11.5424 11.6896 11.3237 11.9084C11.1049 12.1272 10.8081 12.2501 10.4987 12.2501H3.4987C3.18928 12.2501 2.89253 12.1272 2.67374 11.9084C2.45495 11.6896 2.33203 11.3928 2.33203 11.0834V4.08341Z"
                                            stroke="black"
                                            strokeWidth="0.583333"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <path
                                            d="M9.33203 1.75V4.08333"
                                            stroke="black"
                                            strokeWidth="0.583333"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <path
                                            d="M4.66797 1.75V4.08333"
                                            stroke="black"
                                            strokeWidth="0.583333"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                        <path
                                            d="M2.33203 6.41675H11.6654"
                                            stroke="black"
                                            strokeWidth="0.583333"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_2846_6297">
                                            <rect width="14" height="14" fill="white" />
                                        </clipPath>
                                    </defs>
                                </svg>
                                <span className="text-[12px] text-[#999999]">Check out</span>
                            </div>
                            <div className="text-end">
                                <div className="font-normal text-[18px] leading-none mb-[6px]">
                                    {formatDate(checkOut)}
                                </div>
                                <div className="text-[14px] font-medium leading-none text-[#636363]">
                                    11:00 AM
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* Divider */}
                    <div className='flex my-[20px] items-center justify-center'>
                        <svg width="100%" height="1px">
                            <line
                                x1="0" y1="1" x2="100%" y2="1"
                                stroke="#f0f0f0"
                                strokeWidth="2"
                                strokeDasharray="10 4"
                            />
                        </svg>
                    </div>
                    {/* Room Details */}
                    <div className="flex gap-[8px]">
                        <img
                            src={roomImage || '/assets/logo.svg'}
                            alt={roomName}
                            className="w-[60px] h-[60px] rounded-[9px] object-cover"
                        />
                        <div className="flex-1 py-[6px]">
                            <p className="text-[18px] font-medium text-black mb-[8px] leading-none">
                                {roomQuantity} x {roomName}
                            </p>
                            <div className="flex flex-wrap gap-[12px] text-[12px] text-[#bebebe] mb-[8px] leading-[1.1]">
                                <span className='flex items-center gap-[4px]'><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                    <g clip-path="url(#clip0_3371_549)">
                                        <path d="M2.91699 5.24998C2.91699 5.5594 3.03991 5.85614 3.2587 6.07494C3.47749 6.29373 3.77424 6.41665 4.08366 6.41665C4.39308 6.41665 4.68982 6.29373 4.90862 6.07494C5.12741 5.85614 5.25033 5.5594 5.25033 5.24998C5.25033 4.94056 5.12741 4.64381 4.90862 4.42502C4.68982 4.20623 4.39308 4.08331 4.08366 4.08331C3.77424 4.08331 3.47749 4.20623 3.2587 4.42502C3.03991 4.64381 2.91699 4.94056 2.91699 5.24998Z" stroke="black" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round" />
                                        <path d="M12.8337 9.91669V8.16669H1.16699" stroke="black" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round" />
                                        <path d="M1.16699 4.66669V9.91669" stroke="black" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round" />
                                        <path d="M7 8.16667H12.8333V7C12.8333 6.53587 12.649 6.09075 12.3208 5.76256C11.9926 5.43437 11.5475 5.25 11.0833 5.25H7V8.16667Z" stroke="black" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round" />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_3371_549">
                                            <rect width="14" height="14" fill="white" />
                                        </clipPath>
                                    </defs>
                                </svg> 1 {bedType}</span>
                                <span className='flex items-center gap-[4px]'><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                    <g clip-path="url(#clip0_3371_557)">
                                        <path d="M2.91699 4.08333C2.91699 4.70217 3.16282 5.29566 3.60041 5.73325C4.03799 6.17083 4.63149 6.41667 5.25033 6.41667C5.86916 6.41667 6.46266 6.17083 6.90024 5.73325C7.33783 5.29566 7.58366 4.70217 7.58366 4.08333C7.58366 3.46449 7.33783 2.871 6.90024 2.43342C6.46266 1.99583 5.86916 1.75 5.25033 1.75C4.63149 1.75 4.03799 1.99583 3.60041 2.43342C3.16282 2.871 2.91699 3.46449 2.91699 4.08333Z" stroke="black" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round" />
                                        <path d="M1.75 12.25V11.0833C1.75 10.4645 1.99583 9.871 2.43342 9.43342C2.871 8.99583 3.46449 8.75 4.08333 8.75H6.41667C7.0355 8.75 7.629 8.99583 8.06658 9.43342C8.50417 9.871 8.75 10.4645 8.75 11.0833V12.25" stroke="black" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round" />
                                        <path d="M9.33301 1.82581C9.83492 1.95431 10.2798 2.24621 10.5975 2.65549C10.9151 3.06476 11.0876 3.56812 11.0876 4.08622C11.0876 4.60432 10.9151 5.10769 10.5975 5.51696C10.2798 5.92623 9.83492 6.21813 9.33301 6.34664" stroke="black" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round" />
                                        <path d="M12.25 12.25V11.0834C12.247 10.5684 12.0738 10.0688 11.7572 9.66259C11.4407 9.25636 10.9986 8.96627 10.5 8.83752" stroke="black" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round" />
                                    </g>
                                    <defs>
                                        <clipPath id="clip0_3371_557">
                                            <rect width="14" height="14" fill="white" />
                                        </clipPath>
                                    </defs>
                                </svg> Sleeps {sleeps}</span>
                            </div>
                            <span className='flex items-center gap-[4px] text-[12px] text-[#78736A] leading-[1.1]'><svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <g clip-path="url(#clip0_3371_565)">
                                    <path d="M8.16699 7V7.00583" stroke="#78736A" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round" />
                                    <path d="M1.75 12.25H12.25" stroke="#78736A" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round" />
                                    <path d="M3.5 12.25V2.91667C3.5 2.60725 3.62292 2.3105 3.84171 2.09171C4.0605 1.87292 4.35725 1.75 4.66667 1.75H9.33333C9.64275 1.75 9.9395 1.87292 10.1583 2.09171C10.3771 2.3105 10.5 2.60725 10.5 2.91667V12.25" stroke="#78736A" stroke-width="1.16667" stroke-linecap="round" stroke-linejoin="round" />
                                </g>
                                <defs>
                                    <clipPath id="clip0_3371_565">
                                        <rect width="14" height="14" fill="white" />
                                    </clipPath>
                                </defs>
                            </svg> {roomQuantity} Room {sleeps * roomQuantity} guest</span>
                        </div>
                    </div>
                </div>

                {/* Fare Summary Card */}
                <div className="bg-white rounded-[14px] p-[20px] md:p-[28px] shadow-sm ">
                    <h3 className="text-[18px] font-medium text-black leading-[1.1]">Fare Summary</h3>
                    {/* Divider */}
                    <div className='flex my-[20px] items-center justify-center'>
                        <svg width="100%" height="1px">
                            <line
                                x1="0" y1="1" x2="100%" y2="1"
                                stroke="#f0f0f0"
                                strokeWidth="2"
                                strokeDasharray="10 4"
                            />
                        </svg>
                    </div>
                    <div className="space-y-[20px]">
                        <div className="flex justify-between items-center leading-none">
                            <span className="text-[16px] text-[#7e7e7e] ">
                                {roomQuantity} Room, {nights} {nights === 1 ? 'Night' : 'Nights'}
                            </span>
                            <span className="text-[16px] text-[#636363]">
                                ₹{roomPrice.toLocaleString('en-IN')}
                            </span>
                        </div>
                        <div className="flex justify-between items-center leading-none">
                            <span className="text-[16px] text-[#7e7e7e] ">
                                Included Taxes & Charges
                            </span>
                            <span className="text-[16px] text-[#636363]">
                                ₹{taxes.toLocaleString('en-IN')}
                            </span>
                        </div>
                        {discount > 0 && (
                            <div className="flex justify-between items-center leading-none  ">
                                <span className="text-[16px] text-[#7e7e7e]  ">Discounts</span>
                                <span className="text-[16px] text-[#10B981]">
                                    -₹{discount.toLocaleString('en-IN')}
                                </span>
                            </div>
                        )}
                        <div className="flex justify-between items-center leading-none">
                            <span className="text-[16px] text-[#7e7e7e]">Total Payable</span>
                            <span className="text-[20px] text-black">
                                ₹{total.toLocaleString('en-IN')}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Traveller Details Card */}
                <div className="bg-white rounded-[14px] p-[20px] md:p-[28px] shadow-sm ">
                    <h3 className="text-[18px] font-medium text-black  leading-none">Traveller Details</h3>
                    {/* Divider */}
                    <div className='flex my-[20px] items-center justify-center'>
                        <svg width="100%" height="1px">
                            <line
                                x1="0" y1="1" x2="100%" y2="1"
                                stroke="#f0f0f0"
                                strokeWidth="2"
                                strokeDasharray="10 4"
                            />
                        </svg>
                    </div>
                    <div className="space-y-[20px]">
                        <div>
                            <label className="text-[16px] text-[#9d9d9d] mb-[8px] block leading-none">First Name</label>
                            <p className="text-[16px] text-black leading-none">{firstName || 'N/A'}</p>
                        </div>
                        <div>
                            <label className="text-[16px] text-[#9d9d9d] mb-[8px] block leading-none">Last Name</label>
                            <p className="text-[16px] text-black leading-none">{lastName || 'N/A'}</p>
                        </div>
                        <div>
                            <label className="text-[16px] text-[#9d9d9d] mb-[8px] block leading-none">Email Address</label>
                            <p className="text-[16px]  text-black leading-none">
                                {guestInfo.email || 'N/A'}
                            </p>
                        </div>
                        <div>
                            <label className="text-[16px] text-[#9d9d9d] mb-[8px] block leading-none">Mobile No</label>
                            <p className="text-[16px] text-black leading-none">
                                {guestInfo.mobile || guestInfo.phone || 'N/A'}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BookingDetail;

